/**
 * 広告関連の定数。
 *
 * 設計方針:
 *   - 広告 ID / モード / 表示時間はすべてここまたは環境変数 (`VITE_AD_*`) に集約。
 *   - `services/ads.ts` がここの定数を消費し、AdMob 互換 IF を提供する。
 *   - AdMob ID は `.env.android` / `.env.ios` でプラットフォーム別に管理する。
 *   - LINE Mini App の Yahoo 広告タグ ID は `VITE_YAHOO_AD_*` で管理する。
 *
 * 参考ルール: `.claude/rules/no-hardcode.md`
 */

// ---------------------------------------------------------------------------
// モード / Ad Unit ID
// ---------------------------------------------------------------------------

/**
 * 広告モード。
 * - `mock`       : ID 未発行の段階で使うダミー UI（オーバーレイで「広告再生中」風表示）
 * - `production` : @capacitor-community/admob で本番広告（Capacitor Native のみ）
 */
export type AdMode = 'mock' | 'production'

/** `import.meta.env.VITE_AD_MODE` を AdMode へ正規化する。未指定 / 不正値は `mock`。 */
export function getAdMode(): AdMode {
  const raw = (import.meta.env.VITE_AD_MODE ?? '').toLowerCase()
  return raw === 'production' ? 'production' : 'mock'
}

/** リワード広告ユニット ID（環境変数経由）。未設定なら空文字列。 */
export function getRewardedAdUnitId(): string {
  return (import.meta.env.VITE_ADMOB_REWARDED_ID ?? '').trim()
}

/** バナー広告ユニット ID（環境変数経由）。未設定なら空文字列。 */
export function getBannerAdUnitId(): string {
  return (import.meta.env.VITE_ADMOB_BANNER_ID ?? '').trim()
}

/** インタースティシャル広告ユニット ID。 */
export function getInterstitialAdUnitId(): string {
  return (import.meta.env.VITE_ADMOB_INTERSTITIAL_ID ?? '').trim()
}

export type AdScreen = 'title' | 'ranking' | 'result'

export type BannerSize = 'standard' | 'large'

export const BANNER_HEIGHTS: Record<BannerSize, number> = {
  standard: 50,
  large: 90,
}

const YAHOO_BANNER_TAG_IDS: Record<AdScreen, string> = {
  title: (import.meta.env.VITE_YAHOO_AD_BANNER_TITLE_ID ?? '').trim(),
  ranking: (import.meta.env.VITE_YAHOO_AD_BANNER_RANKING_ID ?? '').trim(),
  result: (import.meta.env.VITE_YAHOO_AD_BANNER_RESULT_ID ?? '').trim(),
}

export function getYahooBannerTagId(screen: AdScreen): string {
  return YAHOO_BANNER_TAG_IDS[screen] || ''
}

export function getYahooRewardedTagId(slot: AdSlotType): string {
  const staminaTagId = (import.meta.env.VITE_YAHOO_AD_REWARDED_STAMINA_ID ?? '').trim()
  if (slot === 'stamina') {
    return staminaTagId
  }
  return (import.meta.env.VITE_YAHOO_AD_REWARDED_DAILY_BONUS_ID ?? '').trim() || staminaTagId
}

/** AdMob 本番経路の watchdog。Dismissed が来ない SDK 異常時に音声復帰を保証する。 */
export const AD_DISMISS_TIMEOUT_MS = 60000

/** AdMob リワード事前読み込みのリトライ設定。 */
export const AD_PRELOAD_MAX_RETRY_COUNT = 3
export const AD_PRELOAD_RETRY_BASE_MS = 2000
export const AD_PRELOAD_RETRY_MAX_MS = 30000

// ---------------------------------------------------------------------------
// モック広告のタイミング
// ---------------------------------------------------------------------------

/**
 * モック広告オーバーレイの表示時間（ミリ秒）。
 * 本番 AdMob のリワード広告は 15-30 秒が一般的なので、UX を近づけるため 5 秒に設定。
 */
export const AD_MOCK_REWARDED_DURATION_MS = 5000

/**
 * モック広告のスキップボタンが押せるようになるまでの時間（ミリ秒）。
 * AdMob 本番は最低 5 秒だが、モックでは UX 確認用に 3 秒。
 */
export const AD_MOCK_SKIP_AFTER_MS = 3000

/**
 * モック広告のフォールバック（オーバーレイ未マウント時の confirm 表示）で
 * 「広告を見終えた」までの疑似待機時間。ほぼレガシー扱いだが、SSR / 初期化前の
 * 安全装置として残す。
 */
export const AD_MOCK_CONFIRM_DELAY_MS = 1500

// ---------------------------------------------------------------------------
// 表示シーンとメッセージ
// ---------------------------------------------------------------------------

/** リワード広告を呼び出す目的（スロット）。 */
export type AdSlotType = 'stamina' | 'dailyBonus'

interface AdSlotMeta {
  /** ユーザーに広告視聴の意義を伝えるテキスト（オーバーレイで表示）。 */
  promptMessage: string
  /** 視聴完了時に画面下に出す確認テキスト。 */
  successMessage: string
  /** confirm フォールバック時のダイアログ文言。 */
  confirmFallbackMessage: string
}

/**
 * スロット別のメッセージ。新スロットを追加した場合は型エラーで強制的に追加される。
 */
export const AD_SLOT_META: Record<AdSlotType, AdSlotMeta> = {
  stamina: {
    promptMessage: 'スタミナを全回復するために動画を視聴します',
    successMessage: 'スタミナが全回復しました!',
    confirmFallbackMessage: '広告を視聴してスタミナを全回復しますか?',
  },
  dailyBonus: {
    promptMessage: '今日のボーナススタミナを受け取るために動画を視聴します',
    successMessage: '今日のボーナススタミナを受け取りました!',
    confirmFallbackMessage: '広告を視聴して今日のボーナススタミナを受け取りますか?',
  },
}

// ---------------------------------------------------------------------------
// 後方互換エイリアス（既存呼び出し元の参照を壊さないため）
// ---------------------------------------------------------------------------

/** @deprecated `AD_MOCK_CONFIRM_DELAY_MS` を使う。 */
export const AD_WATCH_SIMULATED_DELAY_MS = AD_MOCK_CONFIRM_DELAY_MS

/** @deprecated `AD_SLOT_META.stamina.confirmFallbackMessage` を使う。 */
export const AD_STUB_MESSAGE_STAMINA = AD_SLOT_META.stamina.confirmFallbackMessage
